import { collection, doc, getDoc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import permissionService from './permissionService';

const rolePermissionsCollection = collection(db, "role_permissions");

// Cache for user role data to avoid repeated fetches
const userRoleCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const rolePermissionService = {
    checkPermission: async ({ permissionName }) => {
        try {
            const getCurrentUser = () =>
              new Promise((resolve) => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                  unsubscribe();
                  resolve(user);
                });
              });
      
            const currentUser = auth.currentUser || await getCurrentUser();
      
            if (!permissionName || !currentUser) {
              return false;
            }
      
            // Check cache for user role
            const cacheKey = currentUser.uid;
            const cachedData = userRoleCache.get(cacheKey);
            let roleId;
            
            if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
                // console.log("Cached role ID:", cachedData);
                roleId = cachedData.roleId;
            } else {
                // Get user's role from users collection
                const userDoc = doc(db, "users", currentUser.uid);
                const userSnapshot = await getDoc(userDoc);
          
                if (!userSnapshot.exists()) {
                  console.log("User not found");
                  return false;
                }
          
                roleId = userSnapshot.data().role_id;
                
                // Cache the role data
                userRoleCache.set(cacheKey, {
                    roleId,
                    timestamp: Date.now()
                });
            }
      
            // Get permission ID
            const permissionId = await permissionService.getIdByName(permissionName);
            if (!permissionId) {
              return false;
            }
      
            const q = query(
              rolePermissionsCollection,
              where("role_id", "==", roleId),
              where("permission_id", "==", permissionId)
            );
      
            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty;
        } catch (error) {
            console.error("Error checking permission:", error);
            return false;
        }
    },

    getRolePermissions: async ({roleId}) => {
        try {
            const q = query(
                rolePermissionsCollection,
                where("role_id", "==", roleId)
            );

            const querySnapshot = await getDocs(q);
            const permissions = await Promise.all(
                querySnapshot.docs.map(async (doc) => {
                    const data = doc.data();
                    const permissionName = await permissionService.getNameById(data.permission_id);
                    return {
                        id: doc.id,
                        permissionName,
                    };
                })
            );

            return permissions;
        } catch (error) {
            console.error('Error getting role permissions:', error);
            throw error;
        }
    },

    getRolePermissionsCount: async (roleId) => {
        try {
            const q = query(
                rolePermissionsCollection,
                where("role_id", "==", roleId)
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.size;
        } catch (error) {
            console.error('Error getting role permissions count:', error);
            return 0;
        }
    },

    updateRolePermissions: async ({ roleId, permissions }) => {
        try {
            // Get current permissions
            const q = query(
                rolePermissionsCollection,
                where("role_id", "==", roleId)
            );
            const currentPermissionsSnapshot = await getDocs(q);
            
            // Start a batch write
            const batch = writeBatch(db);

            // Delete all current permissions
            currentPermissionsSnapshot.docs.forEach((doc) => {
                batch.delete(doc.ref);
            });

            // Add new permissions
            for (const permissionName of permissions) {
                const permissionId = await permissionService.getIdByName(permissionName);
                if (permissionId) {
                    const newPermissionRef = doc(rolePermissionsCollection);
                    batch.set(newPermissionRef, {
                        role_id: roleId,
                        permission_id: permissionId
                    });
                }
            }

            // Commit the batch
            await batch.commit();
            return true;
        } catch (error) {
            console.error('Error updating role permissions:', error);
            throw error;
        }
    },

    deleteRolePermissions: async (roleId) => {
        try {
            const q = query(
                rolePermissionsCollection,
                where("role_id", "==", roleId)
            );
            const querySnapshot = await getDocs(q);
            
            // Start a batch write
            const batch = writeBatch(db);
            
            // Delete all permissions for this role
            querySnapshot.docs.forEach((doc) => {
                batch.delete(doc.ref);
            });
            
            // Commit the batch
            await batch.commit();
            return true;
        } catch (error) {
            console.error('Error deleting role permissions:', error);
            throw error;
        }
    },

    // Clear user role cache (useful when user role changes)
    clearUserRoleCache: (userId = null) => {
        if (userId) {
            userRoleCache.delete(userId);
        } else {
            userRoleCache.clear();
        }
    },

    // Batch check multiple permissions for better performance
    checkMultiplePermissions: async ({ permissionNames }) => {
        try {
            const getCurrentUser = () =>
              new Promise((resolve) => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                  unsubscribe();
                  resolve(user);
                });
              });
      
            const currentUser = auth.currentUser || await getCurrentUser();
      
            if (!permissionNames?.length || !currentUser) {
              return {};
            }
      
            // Check cache for user role
            const cacheKey = currentUser.uid;
            const cachedData = userRoleCache.get(cacheKey);
            let roleId;
            
            if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
                roleId = cachedData.roleId;
            } else {
                // Get user's role from users collection
                const userDoc = doc(db, "users", currentUser.uid);
                const userSnapshot = await getDoc(userDoc);
          
                if (!userSnapshot.exists()) {
                  console.log("User not found");
                  return {};
                }
          
                roleId = userSnapshot.data().role_id;
                
                // Cache the role data
                userRoleCache.set(cacheKey, {
                    roleId,
                    timestamp: Date.now()
                });
            }

            // Get all permission IDs
            const permissionIds = await Promise.all(
                permissionNames.map(name => permissionService.getIdByName(name))
            );

            // Create a map of permission name to ID
            const permissionMap = new Map();
            permissionNames.forEach((name, index) => {
                if (permissionIds[index]) {
                    permissionMap.set(name, permissionIds[index]);
                }
            });

            // Query all role permissions for this role
            const q = query(
                rolePermissionsCollection,
                where("role_id", "==", roleId)
            );
            
            const querySnapshot = await getDocs(q);
            const rolePermissionIds = new Set(
                querySnapshot.docs.map(doc => doc.data().permission_id)
            );

            // Build result object
            const result = {};
            permissionNames.forEach(permissionName => {
                const permissionId = permissionMap.get(permissionName);
                result[permissionName] = permissionId ? rolePermissionIds.has(permissionId) : false;
            });

            return result;
        } catch (error) {
            console.error("Error checking multiple permissions:", error);
            return {};
        }
    }
};

export default rolePermissionService;